import { useEffect, useRef, useState } from 'react';

// Libs
import PlacesAutocomplete, { Suggestion } from 'react-places-autocomplete';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { useScript } from 'usehooks-ts';

// RDS
import Alert from '@ramsey-design-system/alert';
import Button from '@ramsey-design-system/button';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import Textarea from '@ramsey-design-system/textarea';
import { ErrorOutlinedIcon, SearchIcon } from '@ramsey-design-system/icons';

// Components
import CoordinatorProfileImage from '~fpu/shared/components/CoordinatorProfileImage';

// Hooks
import { useCoordinatorProfile } from '~fpu/shared/hooks/useCoordinatorProfile';
import { useUpdateCoordinatorProfile } from '~fpu/modules/FpuDashboard/hooks/useUpdateCoordinatorProfile';

// Utils
import ContentHeader from '~fpu/shared/components/ContentHeader';
import { API_KEYS } from '~fpu/config/constants';
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import { toast } from 'sonner';
import { useClassesByUser } from '~fpu/shared/hooks/useClassesByUser';
import { useUserQuery } from '~fpu/shared/global-queries';

type EditProfileFormValues = {
  hometown: string;
  about: string;
  whyILead: string;
};

function ProfileEdit() {
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [hometown, setHometown] = useState('');
  const [about, setAbout] = useState('');
  const [whyILead, setWhyILead] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: user } = useUserQuery();
  const { data: userClasses } = useClassesByUser(user?.axid);

  const inputRef = useRef<HTMLInputElement>(null);

  const status = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${API_KEYS.GOOGLE_MAPS}&libraries=places&callback=noop`,
    { removeOnUnmount: false }
  );
  const isScriptLoaded = status === 'ready';

  const updateProfile = useUpdateCoordinatorProfile();

  const { data } = useCoordinatorProfile();

  const { handleSubmit, setValue } = useForm<EditProfileFormValues>();

  useEffect(() => {
    if (data) {
      setHometown(data.metadata.hometown);
      setAbout(data.metadata.about === 'undefined' ? '' : data.metadata.about);
      setWhyILead(
        data.metadata.why_i_lead === 'undefined' ? '' : data.metadata.why_i_lead
      );
      setValue('hometown', data.metadata.hometown);
      setValue('about', data.metadata.about);
      setValue('whyILead', data.metadata.why_i_lead);
    }
  }, [data]);

  function handleAboutChange(event: { target: { value: string } }) {
    const value = event.target.value;
    setAbout(value);
    setValue('about', value);
  }

  function handleWhyChange(event: { target: { value: string } }) {
    const value = event.target.value;
    setWhyILead(value);
    setValue('whyILead', value);
  }

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    // Ensure the file < 5 MB
    if (file.size <= 5.24288e6) {
      setProfileImage(file);
      setFilename(file.name);
    } else {
      const message =
        'The image you selected is too large. Please reduce the size or select a smaller image.';
      toast.error(message, {
        duration: 6000
      });
    }
  }

  const onSubmit = (data: EditProfileFormValues) => {
    const formData = new FormData();
    formData.append('hometown', data.hometown || '');
    formData.append('about', data.about || '');
    formData.append('why_i_lead', data.whyILead || '');

    if (profileImage) {
      formData.append('profileImage', profileImage, filename);
    }

    setIsSubmitting(true);

    updateProfile.mutate(formData, {
      onSuccess: () => {
        toast.success('Profile updated successfully', {
          duration: 6000,
          closeButton: true
        });
        setIsSubmitting(false);
      },
      onError: (error) => {
        if (error instanceof Error) {
          toast.error('Unable to save your profile. Please try again.', {
            duration: 10000,
            closeButton: true
          });
        }
        setIsSubmitting(false);
      }
    });
  };

  const renderSuggestion = (suggestion: Suggestion) => {
    const { mainText, secondaryText } = suggestion.formattedSuggestion;
    const index = secondaryText.indexOf(', USA');
    const stateResult =
      index !== -1 ? secondaryText.substring(0, index) : secondaryText;
    return (
      <div>
        {mainText}, {stateResult}
      </div>
    );
  };

  const handleSelect = (selection: string) => {
    const value = selection.replace(', USA', '');
    setHometown(value);
    setValue('hometown', value);
  };

  const handleFileSelectClick = () => inputRef.current?.click();

  if (!userClasses) {
    return null;
  }

  const hasEnrolledClasses = userClasses?.enrolled_cohorts.length > 0;
  const hasOwnedClasses = userClasses?.owned_cohorts.length > 0;

  const allowEdit = hasEnrolledClasses || hasOwnedClasses;

  return (
    <>
      <ContentHeader
        title="Coordinator Public Profile"
        subtext={
          <>
            This helps people searching for a class to learn a little more about
            you and why they should join <em>your</em> class.
          </>
        }
      />

      <section className="mb-4 w-full rounded bg-white p-2 sm:p-3 xl:p-4">
        {!allowEdit && (
          <Alert
            fullWidth
            color="negative"
            icon={ErrorOutlinedIcon}
            className="mb-4"
          >
            You must create a class prior to updating your coordinator profile.
          </Alert>
        )}

        <div className="mb-2 flex flex-row flex-wrap justify-between">
          <div className="w-full md:w-[calc(50%_-_1rem)]">
            <form
              className="gap-md flex flex-col"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="gap-sm flex flex-col">
                <CoordinatorProfileImage
                  size="lg"
                  className="mb-1 shadow-sm sm:h-16 sm:w-16"
                  imageUrl={
                    profileImage
                      ? URL.createObjectURL(profileImage)
                      : data?.metadata.image_url
                  }
                />

                <input
                  type="file"
                  id="image"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageChange}
                  hidden
                  ref={inputRef}
                />

                <Button
                  type="button"
                  size="small"
                  appearance="ghost"
                  className="self-start"
                  disabled={!allowEdit}
                  data-testid="image-select-button"
                  onClick={handleFileSelectClick}
                >
                  Update Profile Image
                </Button>
                <p>Note: Image must be smaller than 5 MB.</p>
              </div>

              {isScriptLoaded && (
                <FormField label="Hometown" className="font-semibold">
                  {(props) => (
                    <PlacesAutocomplete
                      {...props}
                      onChange={(value) => setHometown(value)}
                      onSelect={handleSelect}
                      value={hometown}
                      searchOptions={{
                        componentRestrictions: { country: 'us' },
                        types: ['locality', 'sublocality_level_1']
                      }}
                      googleCallbackName="noop"
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading
                      }) => (
                        <div className="rds-Input">
                          <Input
                            {...getInputProps({
                              placeholder: 'Enter a location'
                            })}
                            icon={SearchIcon}
                            data-testid="location-field"
                          />

                          <div className="autocomplete-dropdown-container">
                            {loading && <div>Loading...</div>}

                            {suggestions.map((suggestion) => {
                              const className = suggestion.active
                                ? 'suggestion-item--active'
                                : 'suggestion-item';
                              const style = suggestion.active
                                ? {
                                    backgroundColor: '#fafafa',
                                    cursor: 'pointer'
                                  }
                                : {
                                    backgroundColor: '#ffffff',
                                    cursor: 'pointer'
                                  };

                              const { key, ...suggestionItemProps } =
                                getSuggestionItemProps(suggestion, {
                                  className,
                                  style
                                });

                              return (
                                <div
                                  key={key}
                                  {...suggestionItemProps}
                                  data-testid="location-suggestion"
                                  className="w-full border border-[#e8eced] p-2 text-sm shadow-sm"
                                >
                                  <span
                                    className="text-sm"
                                    data-testid="location-selection-list"
                                  >
                                    {renderSuggestion(suggestion)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </PlacesAutocomplete>
                  )}
                </FormField>
              )}

              <FormField label="About Me" className="font-semibold">
                {(props) => (
                  <Textarea
                    {...props}
                    className="font-normal"
                    size="lg"
                    maxLength={140}
                    autoGrow={true}
                    value={about}
                    onChange={handleAboutChange}
                    data-testid="about-me-field"
                  />
                )}
              </FormField>

              <FormField label="Why I Lead" className="mb-4 font-semibold">
                {(props) => (
                  <Textarea
                    {...props}
                    className="font-normal"
                    size="lg"
                    maxLength={140}
                    autoGrow={true}
                    value={whyILead}
                    onChange={handleWhyChange}
                    data-testid="why-i-lead-field"
                  />
                )}
              </FormField>

              <div className="flex flex-row items-center">
                <Button
                  size="medium"
                  type="submit"
                  className="mr-2 !bg-blue-50"
                  data-testid="update-profile-button"
                  loading={isSubmitting}
                  disabled={!allowEdit}
                  onClick={() =>
                    analyticsLogger.trackElementClicked({
                      element: 'Update Profile',
                      label: 'Update'
                    })
                  }
                >
                  {isSubmitting ? 'Saving...' : 'Update Profile'}
                </Button>

                <Button
                  appearance="subtle"
                  element={Link}
                  to="/dashboard"
                  data-testid="cancel-button"
                  onClick={() =>
                    analyticsLogger.trackElementClicked({
                      element: 'Update Profile',
                      label: 'Cancel'
                    })
                  }
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default ProfileEdit;
export { ProfileEdit };
